import { Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CalendarPlus, Settings, Shield } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "utils";

import { AppShell } from "components/AppShell";
import { CardGrid } from "components/CardGrid";
import { ToggleChip } from "components/ToggleChip";
import { EventMeta } from "components/EventMeta";
import { UnpublishedBadge } from "components/EventSummaryCard";
import { EmptyState, LoadingRows } from "components/states";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { UNPUBLISHED_ADMIN_NOTE } from "components/unpublished-note";

import { cardTiming } from "../dashboard/arrange-memberships";
import type { MembershipCard } from "../dashboard/schemas";
import { ArchivedChip, EndedChip } from "../dashboard/EventTileParts";
import { useNow } from "../dashboard/use-today";
import {
  eventHasEnded,
  isArchived,
  sortNewestFirst,
  splitPast,
} from "../events/event-past";
import { PastSection } from "../events/PastSection";
import { NewEventSection } from "../governance/GovernancePage";
import { useUserProfile } from "../profile/use-user-profile";
import { useAdminEvents } from "./use-admin-events";

export function AdminLandingPage({
  uid,
  email,
  signOut,
}: {
  uid: string;
  email: string | null;
  signOut: () => void;
}) {
  const events = useAdminEvents(uid);
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  // D11: an event admin may create an event. The predicate cannot be "is an
  // event admin", because the person who most needs this is the one this page
  // is currently telling they administer nothing — so it is the platform role,
  // which is also what `governance_commands` now gates CREATE_EVENT on. A
  // profile that is still loading shows no button rather than a wrong one.
  const { state: profile } = useUserProfile(uid);
  const mayCreateEvents =
    profile.status === "success" &&
    (profile.profile.platformRole === "admin" ||
      profile.profile.platformRole === "super_admin");

  const createButton = mayCreateEvents ? (
    <Button
      variant="cta"
      size="sm"
      onClick={() => setCreating(true)}
      data-testid="create-event"
    >
      <CalendarPlus className="h-4 w-4" />
      Create event
    </Button>
  ) : undefined;

  return (
    <AppShell
      identity={{ email }}
      onSignOut={signOut}
      title="Event Administration"
      actions={createButton}
    >
      {events.status === "loading" && <LoadingRows rows={3} />}

      {events.status === "error" && (
        <EmptyState icon={AlertCircle} title="Could not load events">
          {events.error}
        </EmptyState>
      )}

      {events.status === "success" && events.data.length === 0 && (
        <EmptyState
          icon={Settings}
          title="No admin events"
          testId="no-admin-events"
        >
          {mayCreateEvents
            ? "You do not administer an event yet. Create one and you will be its administrator."
            : "You are not an administrator of any events yet."}
        </EmptyState>
      )}

      {events.status === "success" && events.data.length > 0 && (
        <AdminEventsList cards={events.data} />
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create event</DialogTitle>
          </DialogHeader>
          <NewEventSection
            uid={uid}
            // Straight into the event rather than back to this list. The
            // creator's admin membership is written in the same transaction
            // that creates the event, and the command only reports SUCCEEDED
            // once that has committed, so the page they land on can already
            // read them as its admin. `useAdminEvents` is a one-shot read and
            // would otherwise show a list without the event they just made.
            onCreated={(eventId) => {
              setCreating(false);
              void navigate({
                to: "/admin/event/$eventId",
                params: { eventId },
              });
            }}
          />
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

/**
 * The administered events: live and upcoming first, then "Past". Archived
 * events are left out of both, and shown alone under the "Archived" chip, so
 * a long-finished event an organiser put away does not sit in their list.
 */
function AdminEventsList({ cards }: { cards: MembershipCard[] }) {
  const now = useNow();
  const [showArchived, setShowArchived] = useState(false);
  const [showAllPast, setShowAllPast] = useState(false);

  const { active, archived } = useMemo(() => {
    const archivedCards = cards.filter((c) => isArchived(cardTiming(c)));
    return {
      active: cards.filter((c) => !isArchived(cardTiming(c))),
      archived: sortNewestFirst(archivedCards, cardTiming),
    };
  }, [cards]);
  const split = useMemo(
    () => splitPast(active, cardTiming, now, { showAll: showAllPast }),
    [active, now, showAllPast],
  );

  // Never a chip that leads to an empty list, but a chip that is on stays so
  // it can be turned off.
  const chip =
    archived.length > 0 || showArchived ? (
      <div className="mb-4">
        <ToggleChip
          selected={showArchived}
          onClick={() => setShowArchived((shown) => !shown)}
          data-testid="admin-archived-filter"
        >
          Archived ({archived.length})
        </ToggleChip>
      </div>
    ) : null;

  if (showArchived) {
    return (
      <div data-testid="admin-events-list">
        {chip}
        {archived.length === 0 ? (
          <EmptyState
            icon={Settings}
            title="No archived events"
            testId="no-archived-events"
          >
            Events you archive appear here.
          </EmptyState>
        ) : (
          <CardGrid className="gap-4" testId="admin-archived-list">
            {archived.map((card) => (
              <AdminEventCard key={card.eventId} card={card} />
            ))}
          </CardGrid>
        )}
      </div>
    );
  }

  return (
    <div data-testid="admin-events-list">
      {chip}
      {split.current.length > 0 && (
        <CardGrid className="gap-4" testId="admin-current-list">
          {split.current.map((card) => (
            <AdminEventCard key={card.eventId} card={card} />
          ))}
        </CardGrid>
      )}
      {split.current.length === 0 && split.past.length === 0 && (
        <EmptyState
          icon={Settings}
          title="All your events are archived"
          testId="admin-all-archived"
        >
          Use the Archived filter to see them.
        </EmptyState>
      )}
      {split.past.length > 0 && (
        <PastSection
          total={split.pastTotal}
          hasMore={split.hasMorePast}
          expanded={showAllPast}
          onToggle={() => setShowAllPast((shown) => !shown)}
          testId="admin-past"
        >
          <CardGrid className="gap-4" testId="admin-past-list">
            {split.past.map((card) => (
              <AdminEventCard
                key={card.eventId}
                card={card}
                ended={eventHasEnded(cardTiming(card), now)}
              />
            ))}
          </CardGrid>
        </PastSection>
      )}
    </div>
  );
}

/**
 * One administered event. `eventSlug` is set only once the event is published
 * (event_card.py), so a card without one is a draft: muted, badged and told
 * where publishing happens, as on My Events.
 */
function AdminEventCard({
  card,
  ended = false,
}: {
  card: MembershipCard;
  /** Over in the event's own zone, which the list has judged. */
  ended?: boolean;
}) {
  const unpublished = !card.eventSlug;
  return (
    <Link
      to="/admin/event/$eventId"
      params={{ eventId: card.eventId }}
      className="block h-full rounded-lg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div
        className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground wrap-anywhere transition-shadow hover:shadow-card-hover"
        data-testid="admin-event-card"
        data-published={unpublished ? "false" : undefined}
      >
        {card.eventImage && (
          <div className="h-32 shrink-0 overflow-hidden">
            <img
              src={card.eventImage}
              alt={`${card.eventTitle ?? card.eventId} event`}
              className={cn(
                "h-full w-full object-cover",
                unpublished && "opacity-60 grayscale",
              )}
            />
          </div>
        )}
        <div className="p-4">
          <p className="text-lg font-semibold text-foreground">
            {card.eventTitle ?? card.eventId}
          </p>
          <EventMeta
            startDate={card.eventStartDate}
            endDate={card.eventEndDate}
            className="mt-1 text-xs"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge className="bg-hb-purple-fill text-white">
              <Shield className="mr-1 h-3 w-3" aria-hidden="true" />
              Admin
            </Badge>
            {unpublished && <UnpublishedBadge />}
            {ended && <EndedChip />}
            {card.eventArchived === true && <ArchivedChip />}
          </div>
          {unpublished && (
            <p
              className="mt-2 text-xs font-medium text-foreground"
              data-testid="admin-event-card-note"
            >
              {UNPUBLISHED_ADMIN_NOTE}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}
